import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { IoChatbubbleEllipsesOutline, IoClose, IoSend } from 'react-icons/io5';
import axios from 'axios';
import chatbotData from './chatbotData';

const matchQuestion = (input, qaItems) => {
  const words = input.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  if (words.length === 0) return null;

  let bestMatch = null;
  let bestScore = 0;

  for (const item of qaItems) {
    let score = 0;
    for (const word of words) {
      for (const keyword of (item.keywords || [])) {
        if (keyword.includes(word) || word.includes(keyword)) {
          score++;
        }
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = item;
    }
  }

  return bestScore >= 1 ? bestMatch : null;
};

const EVENT_KEYWORDS = ['event', 'events', 'upcoming', 'schedule', 'session', 'activity', 'activities', 'calendar', 'deadline', 'deadlines', 'what\'s happening', 'whats happening'];

const isEventQuery = (input) => {
  const lower = input.toLowerCase();
  return EVENT_KEYWORDS.some(kw => lower.includes(kw));
};

const formatEventDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const SUGGESTIONS = [
  'How do I get started?',
  'How do I submit my advocacy work?',
  'How do I track my progress?',
  'What are learning paths?',
  'Upcoming events'
];

const QUICK_LINKS = [
  { label: 'Access your Profile', url: '/profile' },
  { label: 'Access Messaging area', url: '/messages' },
  { label: 'View your Dashboard', url: '/dashboard' },
  { label: 'Submit my Advocacy Work', url: '/submissions' },
  { label: 'Browse Courses', url: '/dashboard' },
];

const ChatBot = ({ auth }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: 'bot', text: 'Hi! I can help you with questions about the platform. Ask me anything or pick a topic below.' }
  ]);
  const [input, setInput] = useState('');
  const [qaItems, setQaItems] = useState(chatbotData);
  const messagesEndRef = useRef(null);

  const getAuthConfig = () => {
    const config = { headers: { 'Content-Type': 'application/json' } };
    if (auth?.token) config.headers['Authorization'] = 'Bearer ' + auth.token;
    return config;
  };

  useEffect(() => {
    axios.get('/api/chatbot-qa')
      .then(res => {
        if (res.data.length > 0) {
          setQaItems([...res.data, ...chatbotData]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleLinkClick = (url) => {
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener');
    } else {
      setIsOpen(false);
      navigate(url);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      const now = new Date().toISOString();
      const res = await axios.get(`/api/events/list?limit=5&type=event&startDate=${now}`, getAuthConfig());
      const events = res.data.events || res.data;
      const eventList = Array.isArray(events) ? events : [];
      return eventList.filter(e => e.isActive);
    } catch {
      return [];
    }
  };

  const handleSend = async (text) => {
    const question = text || input.trim();
    if (!question) return;

    const userMsg = { from: 'user', text: question };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    // Check if it's an event-related question
    if (isEventQuery(question)) {
      const events = await fetchUpcomingEvents();

      if (events.length > 0) {
        const eventLines = events.map(e =>
          `${e.title}${e.date ? ' — ' + formatEventDate(e.date) : ''}`
        ).join('\n');

        const botMsg = {
          from: 'bot',
          text: `Here are your upcoming events:\n\n${eventLines}`,
          links: events.map(e => ({ label: e.title, url: `/dashboard` }))
        };
        setMessages((prev) => [...prev, botMsg]);
      } else {
        setMessages((prev) => [...prev, {
          from: 'bot',
          text: 'No upcoming events found right now. Check back later or ask your instructor about scheduled activities.'
        }]);
      }
      return;
    }

    // Normal Q&A matching
    const match = matchQuestion(question, qaItems);
    const botMsg = {
      from: 'bot',
      text: match
        ? match.answer
        : "I'm not sure about that. Try asking about courses, submissions, progress, or events. You can also contact your instructor for help.",
      links: match?.links || []
    };

    setMessages((prev) => [...prev, botMsg]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  return (
    <>
      {/* Floating bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-[#1086a4] hover:bg-[#0d6e87] text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg transition-transform hover:scale-105"
          title="Chat with us"
        >
          <IoChatbubbleEllipsesOutline size={26} />
        </button>
      )}

      {/* Chat overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setIsOpen(false)}>
          <div className="w-full max-w-lg mx-4 h-[600px] bg-card border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-[#1086a4] text-white px-5 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-base">Betty Bot</span>
                <button onClick={() => setIsOpen(false)} className="hover:opacity-80">
                  <IoClose size={22} />
                </button>
              </div>
              <p className="text-xs text-white/70 mt-1">Beta — Answers may not always be accurate</p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-lg text-sm leading-relaxed ${
                      msg.from === 'user'
                        ? 'bg-[#1086a4] text-white rounded-br-none'
                        : 'bg-muted text-foreground rounded-bl-none'
                    }`}
                  >
                    {msg.text.split('\n').map((line, k) => (
                      <span key={k}>{line}<br /></span>
                    ))}
                    {msg.links && msg.links.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {msg.links.map((link, j) => (
                          <button
                            key={j}
                            onClick={() => handleLinkClick(link.url)}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-[#1086a4]/10 text-[#1086a4] rounded text-xs font-medium hover:bg-[#1086a4]/20 transition-colors"
                          >
                            {link.label} &rarr;
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Quick links — shown only when no user messages yet */}
              {messages.length === 1 && (
                <div className="grid grid-cols-2 gap-2 pt-2">
                  {QUICK_LINKS.map((link, i) => (
                    <button
                      key={i}
                      onClick={() => handleLinkClick(link.url)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-border text-sm text-foreground hover:bg-muted transition-colors text-left"
                    >
                      {link.label} <span className="ml-auto text-[#1086a4]">&rarr;</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Suggestions — only show after user has sent a message */}
              {messages.length > 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => handleSend(s)}
                      className="text-xs px-3 py-1.5 rounded-full border border-border text-foreground hover:bg-muted transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-border p-4 flex gap-2 flex-shrink-0">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your question..."
                className="flex-1 bg-muted text-foreground text-sm px-3 py-2 rounded-lg border border-border focus:outline-none focus:ring-1 focus:ring-[#1086a4]"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim()}
                className="bg-[#1086a4] text-white px-3 py-2 rounded-lg hover:bg-[#0d6e87] disabled:opacity-40 transition-colors"
              >
                <IoSend size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = state => ({ auth: state.auth });
export default connect(mapStateToProps)(ChatBot);
